import React, { useState } from "react";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import { Link, useNavigate } from "react-router-dom";
import Button from "components/common/atoms/Button";
import HidePassword from "static/images/Hide.svg";
import ShowPassword from "static/images/Show.svg";
import LabelField from "components/common/atoms/LabelField";
import InputTextfield from "components/common/atoms/InputTextField";
import { ApiCall } from "components/common/services/ApiServices";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import { useDispatch } from "react-redux";
import { addUserDetails } from "features/auth/AuthSlice";
import ImageField from "components/common/atoms/ImageField";
import "static/css/login.css";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import {
  validateForm,
  validateEmail,
  validateRequired,
  validatePassword,
  validateLoginEmail,
  // validateCheckbox,
} from "components/common/services/ValidationService";
import CustomNotify from "components/common/atoms/CustomNotify";
import {
  GoogleReCaptchaProvider,
  // GoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { CANDIDATE_ROLE, reCaptchaSiteKey, CENTRAL_DATA_MANAGEMENT_MICROSERVICE } from "Constants";
import { LOGIN, CLARITALK_GET_SOUND_SETTINGS_V2 } from "routes/ApiEndpoints";
import {
  setLanguage,
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import secureLocalStorage from "react-secure-storage";
import { clearActiveTab } from "features/NavigationSlice";
import GetQueryParams from "components/common/services/GetQueryParams";

interface Credentials {
  userEmail?: string;
  email?: string;
  password?: string;
  isCaptcha?: boolean;
}

interface ValidationRules {
  [key: string]: Function[];
}

const LoginForm = () => {
  // Show/hide toggle for the password field (boolean, not a credential).
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [data, setData] = useState<Credentials>({
    userEmail: "",
    email: "",
    password: "",
    isCaptcha: false,
  });

  const [errors, setErrors] = useState<Credentials>({});

  const [loading, setLoading] = useState(false);

  const getUrlParams = GetQueryParams();
  const paramFilter = getUrlParams.get("user");

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setData((prevData) => ({ ...prevData, [name]: value }));
    validation(name, value, true);
  };

  const validation = (
    name: string,
    value: string,
    isSingleFieldValidation: boolean = false
  ) => {
    const validationRules: ValidationRules = {
      email: [validateRequired, validateLoginEmail],
      password: [validateRequired, validatePassword],
      userEmail:
        paramFilter === "master" ? [validateRequired, validateLoginEmail] : [],
      // isCaptcha: [validateCheckbox],
    };

    const validationErrors = validateForm(
      { ...data, [name]: value },
      validationRules,
      isSingleFieldValidation ? name : undefined
    );

    if (isSingleFieldValidation && Object.keys(errors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name],
      }));
    } else {
      setErrors(validationErrors);
    }

    if (Object.keys(validationErrors).length > 0) {
      return false;
    }

    return true;
  };

  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    event.preventDefault();
    const { name, value } = event.target as HTMLInputElement;
    const formData = {
      email: data.email,
      // TO-38127 (f_fesec_d4e29ffeed): send the password as plaintext over HTTPS
      // (backend accepts both via decryptOrPlain). JSON.stringify is kept so the
      // value still matches the stored bcrypt hash; only the client AES is removed.
      password: JSON.stringify(data.password),
      userEmail: data.userEmail,
      loginType: paramFilter ?? "",
    };

    if (validation(name, value)) {
      const response = await ApiCall.service(LOGIN, "POST", formData, true);
      if (response?.status === 200) {
        const getRefreshToken = secureLocalStorage.getItem("refresh_token");
        let token;
        if (getRefreshToken === "" || getRefreshToken === null) {
          token = null;
        } else {
          const refreshTokenObj = JSON.parse(getRefreshToken as string);
          token = refreshTokenObj.token;
        }

        const user = {
          token: response?.access_token,
          email: response?.data["email"],
          role: response?.data["role"],
          userId: response?.data["userId"],
          name: response?.data["name"],
          fullName: response?.data["fullName"],
          profilePicName: response?.data["prof_pic_name"],
          profilePicId: response?.data["profPicId"],
          profilePicPath: response?.data["profPicPath"],
          token_type: response?.token_type,
          isAuthenticated: true,
          refreshToken: token,
          candidateId: response?.data["candidateId"],
          lastLogin: response?.data["lastLogin"],
          office: response?.data["office"],
          officeId: response?.data["officeId"],
          correspondingOffice: response?.data["correspondingOffice"],
          isSignature: response?.data["is_signature"],
          userPrefLang: response?.data["userPrefLang"],
          consentStatus: response?.data["consentStatus"],
          consentToTerms: response?.data["consentToTerms"],
          isInstanceReady: response?.data["isInstanceReady"],
          officePostalCode: response?.data['officePostalCode']
        };
        dispatch(addUserDetails(user));
        dispatch(clearActiveTab());

        if (user.lastLogin === null) {
          navigate("/");
        } else {
          navigate("/dashboard");
        }
        CustomNotify({
          type: "success",
          message: t("Logged in successfully") + "!",
        });
        secureLocalStorage.setItem(
          "site_lang",
          response?.data["userPrefLang"] ?? "en"
        );
        setLanguage(response?.data["userPrefLang"] ?? "en");

        // Silently auto-configure Claritalk sound settings and fetch meeting types
        const clritalkResponse =   await ApiCall.service(
          CLARITALK_GET_SOUND_SETTINGS_V2,
          'POST',
          { userId: user.userId },
          true,
          CENTRAL_DATA_MANAGEMENT_MICROSERVICE
        );
        dispatch(addUserDetails({ claritalkMeetingTypes: clritalkResponse?.meetingTypes }));

      } else {
        if (
          paramFilter === "master" &&
          !response.msg.endsWith("not a master user")
        ) {
          setErrors({ userEmail: response.msg });
        } else {
          setErrors({ email: response.msg });
        }

        CustomNotify({
          type: "error",
          message: t(response.msg),
          autoClose: 2000,
        });
      }
    }
    setLoading(false);
  };

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  // const handleCaptchaVerify = (response: string | null) => {
  //   if (response) {
  //     setData((prevFormData) => ({
  //       ...prevFormData,
  //       isCaptcha: true,
  //     }));
  //   }
  // };

  return (
    <>
      <div className="container-fluid login-bg">
        <div className="row">
          <div className="col-6 px-0 vh-100">
            <ImageField
              altText={t("Banner")}
              className="w-100 h-100 img-fluid object-fit-cover"
              src="/static/images/Welcome-dashboard.png"
            />
          </div>
          <div className="col-6 align-self-center">
            <div style={{ width: "60%", margin: "auto" }}>
              <div className="text-center" style={{ marginBottom: "3vw" }}>
                <ImageField
                  altText={t("Logo")}
                  className="text-center logo-width"
                  src="static/images/logo.svg"
                />
              </div>
              <GoogleReCaptchaProvider reCaptchaKey={reCaptchaSiteKey}>
                <form onSubmit={submitHandler} className="login-form">
                  <div className="text-center" style={{ marginBottom: "2vw" }}>
                    <span className="text-center login-title position-relative">
                      {t("Login")}
                      <img
                        src="../static/images/Splash_1.svg"
                        alt="dot-arrow"
                        className="dot-arrow position-absolute"
                      />
                    </span>
                  </div>
                  {paramFilter === "master" && (
                    <LabelWithInputField
                      label={t("Email")}
                      id="userEmail"
                      isMandatory={true}
                      placeholder={t("Enter email")}
                      type="email"
                      name="userEmail"
                      handleChange={changeHandler}
                      value={data.userEmail}
                      error={errors.userEmail}
                      // autoComplete="userEmail"
                    />
                  )}
                  <LabelWithInputField
                    label={
                      paramFilter === "master"
                        ? t("Master user email")
                        : t("Email")
                    }
                    id="email"
                    isMandatory={true}
                    placeholder={t("Enter email")}
                    type="email"
                    name="email"
                    handleChange={changeHandler}
                    value={data.email}
                    error={errors.email}
                    autoComplete="username"
                  />
                  <div className="col-lg-12">
                    <LabelField
                      title={
                        paramFilter === "master"
                          ? t("Master user password")
                          : t("Password")
                      }
                      isMandatory={true}
                      htmlfor="password"
                    />
                    <div className="d-flex position-relative">
                      <InputTextfield
                        handleChange={changeHandler}
                        type={showPassword ? "text" : "password"}
                        value={data.password}
                        id="password"
                        placeholder={t("Enter password")}
                        name="password"
                        autoComplete="current-password"
                      />
                      <p
                        onClick={togglePassword}
                        className="position-absolute cursor-pointer eye-icon"
                      >
                        {!showPassword ? (
                          <img src={HidePassword} alt={t("Hide Password")} />
                        ) : (
                          <img src={ShowPassword} alt={t("Show Password")} />
                        )}
                      </p>
                    </div>
                    <div className="row">
                      <div
                        className="height-20"
                        style={{ marginBottom: "0.5vw" }}
                      >
                        {errors.password && (
                          <span className="text-danger">{errors.password}</span>
                        )}
                      </div>
                    </div>
                    <div className="row" style={{ marginBottom: "1.6vw" }}>
                      <div className="col-md-12">
                        <div className="ms-auto">
                          <Link
                            to="/forgot-password"
                            className="float-end link-color"
                          >
                            {t("Forgot password") + "?"}
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-center">
                      {/* <GoogleReCaptcha
                      onVerify={handleCaptchaVerify}
                    /> */}
                    </div>
                    <div className="row">
                      <div className="text-center">
                        {errors.isCaptcha && (
                          <span className="text-danger">
                            {t("Please verify that you are not a robot.")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-10 mx-auto text-center">
                      {!loading ? (
                        <Button
                          title={t("Login")}
                          icon={faAngleRight}
                          type="submit"
                          className="form-button w-100"
                          style={{ marginBottom: "1.5vw" }}
                        />
                      ) : (
                        <div>
                          <LoadingIcon
                            iconType="bars"
                            color="#ff4dae"
                            height={"3vw"}
                            width={"3vw"}
                          />
                        </div>
                      )}
                    </div>
                    <div className="col-md-12 text-center">
                      <span>
                        {t("Don't have an account") + "?"}&nbsp;
                        <Link to="/register" className="link-color ms-1">
                          {t("Register as a candidate")}
                        </Link>
                      </span>
                    </div>
                  </div>
                </form>
              </GoogleReCaptchaProvider>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default translate(LoginForm);
